import { useShallow } from "zustand/react/shallow";
import { useElementMotionStore } from "@/lib/stores/element-motion-store/element-motion-store";
import { ElementDto } from "@repo/api/overlays";
import { useCallback, useMemo } from "react";
import { useCurrentScale } from "remotion";
import { ELEMENT_CONTROLS_ROOT_ID } from "./ElementControlsLayer";
import { BoundingBox } from "@/lib/stores/element-motion-store/element-motion-state";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { findElementByExternalId } from "@/lib/stores/editor-store/editor-state";
import { OrderingType } from "./SelectionActions";

export const useMovingElement = (element: ElementDto) => {
  const movingElement = useElementMotionStore(
    useShallow((state) => state.movingElements[element.externalId]),
  );

  return {
    movingElement,
  };
};

export const useUpdateBoundingBox = (element: ElementDto) => {
  const scale = useCurrentScale() || 1;

  const setBoundingBox = useElementMotionStore(
    (state) => state.service.setBoundingBox,
  );

  const updateBoundingBox = useCallback(
    (divElement: HTMLDivElement | null) => {
      const elementControlsRoot = document.getElementById(
        ELEMENT_CONTROLS_ROOT_ID,
      );

      if (!divElement || !elementControlsRoot) {
        setBoundingBox(element.externalId, null);
        return;
      }

      const clientRect = divElement.getBoundingClientRect();
      const compositionRect = elementControlsRoot.getBoundingClientRect();

      const clientRectScaled = {
        left: clientRect.left / scale,
        top: clientRect.top / scale,
        right: clientRect.right / scale,
        bottom: clientRect.bottom / scale,
      };

      const compositionRectScaled = {
        left: compositionRect.left / scale,
        top: compositionRect.top / scale,
      };

      /**
       * A quick hack to fix rounding errors with text elements.
       * The bottom and right values sometimes land on half-pixels.
       * The exact value can vary depending on screen position,
       * even though the element itself doesn’t change.
       * For example: 121.50004958568407 vs 121.4999669428773.
       *
       * Adding a small tolerance fixes the original issue,
       * but really it just shifts the pivot point for rounding.
       * In practice, we haven’t seen problematic cases like
       * 121.4989 vs 121.499 — but if they occurred,
       * the tolerance would round them to 121 and 122.
       * In that case, we have to find a better solution.
       */
      const tolerance = 1e-3;

      setBoundingBox(element.externalId, {
        left: Math.round(clientRectScaled.left - compositionRectScaled.left),
        top: Math.round(clientRectScaled.top - compositionRectScaled.top),
        right: Math.round(
          clientRectScaled.right - compositionRectScaled.left + tolerance,
        ),
        bottom: Math.round(
          clientRectScaled.bottom - compositionRectScaled.top + tolerance,
        ),
      });
    },
    [setBoundingBox, element, scale],
  );

  return updateBoundingBox;
};

export const useBoundingBox = (externalId: string) => {
  const boundingBox = useElementMotionStore(
    useShallow((state) => state.boundingBoxes[externalId]),
  );

  return {
    left: boundingBox?.left,
    top: boundingBox?.top,
    right: boundingBox?.right,
    bottom: boundingBox?.bottom,
  };
};

export const useSelectionBoundingBox = () => {
  const selectionBoundingBox = useElementMotionStore(
    useShallow((state) => state.service.getSelectionBoundingBox()),
  );

  return selectionBoundingBox;
};

export const useGuidelines = (selectionBoundingBox: BoundingBox | null) => {
  const getActiveGuidelines = useElementMotionStore(
    useShallow((state) => state.service.getActiveGuidelines),
  );

  const isMoving = useElementMotionStore(
    (state) =>
      state.reactivePointerState?.type === "ARROW_MOVE_SELECTION" ||
      state.reactivePointerState?.type === "POINTER_MOVE_SELECTION",
  );

  const guidelines = useMemo(() => {
    if (!isMoving) {
      return [];
    }

    return getActiveGuidelines(selectionBoundingBox);
  }, [selectionBoundingBox, getActiveGuidelines, isMoving]);

  return guidelines;
};

export const useSetSelectionFullscreen = () => {
  const selectedElementIds = useElementMotionStore(
    useShallow((state) => state.selectedElementIds),
  );

  const getElementsMapWithSceneAndIndex = useElementMotionStore(
    (state) => state.service.getElementsMapWithSceneAndIndex,
  );

  const setSelectionFullscreen = useElementMotionStore(
    (state) => state.service.setSelectionFullscreen,
  );

  const isVisible = useMemo(() => {
    if (selectedElementIds.size !== 1) {
      return false;
    }

    const elementId = Array.from(selectedElementIds)[0]!;

    const elementsMap = getElementsMapWithSceneAndIndex();
    const element = elementsMap[elementId];

    if (!element) {
      return false;
    }

    return "size" in element.element;
  }, [selectedElementIds, getElementsMapWithSceneAndIndex]);

  return {
    isVisible,
    setSelectionFullscreen,
  };
};

export const useToggleSelectionVisibility = () => {
  const selectedElementIds = useElementMotionStore(
    useShallow((state) => state.selectedElementIds),
  );

  const toggleSelectedElementsVisibility = useElementMotionStore(
    (state) => state.service.toggleSelectedElementsVisibility,
  );

  const allVisible = useEditorStore(
    useShallow((state) => {
      if (selectedElementIds.size === 0) {
        return false;
      }

      return Array.from(selectedElementIds).every(
        (id) => findElementByExternalId(state, id)?.isHidden !== true,
      );
    }),
  );

  return {
    allVisible,
    toggleSelectionVisibility: toggleSelectedElementsVisibility,
  };
};

export const useUpdateElementOrdering = () => {
  const updateElementOrderingEditorStore = useEditorStore(
    (state) => state.service.updateElementOrdering,
  );

  const selectedElementIds = useElementMotionStore(
    useShallow((state) => state.selectedElementIds),
  );

  const getElementsMapWithSceneAndIndex = useElementMotionStore(
    (state) => state.service.getElementsMapWithSceneAndIndex,
  );

  const updateElementOrdering = useCallback(
    (ordering: OrderingType) => {
      const elementsMap = getElementsMapWithSceneAndIndex();

      const startSceneIds: string[] = [];
      const endSceneIds: string[] = [];
      const mainSceneIds: string[] = [];

      for (const id of selectedElementIds) {
        const element = elementsMap[id];

        if (!element) {
          continue;
        }

        if (element.sceneType === "startScene") {
          startSceneIds.push(id);
        } else if (element.sceneType === "endScene") {
          endSceneIds.push(id);
        } else {
          mainSceneIds.push(id);
        }
      }

      updateElementOrderingEditorStore(startSceneIds, "startScene", ordering);
      updateElementOrderingEditorStore(mainSceneIds, "mainScene", ordering);
      updateElementOrderingEditorStore(endSceneIds, "endScene", ordering);
    },
    [
      selectedElementIds,
      updateElementOrderingEditorStore,
      getElementsMapWithSceneAndIndex,
    ],
  );

  return updateElementOrdering;
};

export const useDuplicateElements = () => {
  return useElementMotionStore(
    (state) => state.service.duplicateSelectedElements,
  );
};
